import React, { forwardRef, memo, useEffect, useRef, useState } from 'react'
import { getComponents, getCurrentValue } from './Select.utils'
import styles from './SelectInput.module.scss'
import Select from 'react-select'

const borderSelectColor = (state) => {
  if (state.isDisabled) return '#E7E7EB'
  if (state.isFocused && state.selectProps?.menuIsOpen)
    return `#6553F5 !important`
  if (state.selectProps?.isError) return '#FF3B30'
  return '#DDDDE3'
}

const selectStyles = {
  container: (baseStyles) => ({
    ...baseStyles,
    fontFamily: 'Manrope, sans-serif',
    width: '100%'
  }),
  control: (baseStyles, state) => {
    return {
      ...baseStyles,
      minHeight: '44px',
      borderRadius: '8px',
      border: `0.8px solid ${borderSelectColor(state)}`,
      background: '#FFFFFF',
      boxShadow: 'none',
      fontSize: 15,
      lineHeight: '24px',
      color: '#242424',
      cursor: 'pointer',
      ':hover': {
        borderColor: '#D6D6DE'
      },
      '@media (max-width: 600px)': {
        fontSize: 16
      }
    }
  },
  singleValue: (baseStyled, state) => ({
    ...baseStyled,
    color: state.isDisabled ? '#6A6A71' : '#242424',
    fontSize: 15,
    lineHeight: '20px',
    whiteSpace: 'nowrap',
    '@media (max-width: 600px)': {
      fontSize: 16
    }
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '5px 0 5px 12px',
    gap: '4px 8px',
    '& > div > input': {
      color: `#242424 !important`
    }
  }),

  placeholder: (baseStyled) => ({
    ...baseStyled,
    fontFamily: 'Manrope, sans-serif',
    color: '#6A6A71',
    fontWeight: 400,
    fontSize: 15,
    lineHeight: '20px',
    '@media (max-width: 600px)': {
      fontSize: 16
    }
  }),
  menu: (baseStyled, state) => {
    return {
      ...baseStyled,
      position: state.selectProps.customPositionMenu || 'absolute',
      top: '40px',
      maxHeight: '260px',
      border: `0.5px solid #DDDDE3`,
      boxShadow:
        '0px 4px 8px rgba(0, 0, 0, 0.04), 0px 0px 4px rgba(0, 0, 0, 0.06)',
      zIndex: 1000,
      borderRadius: '8px',
      background: '#FFFFFF'
    }
  },
  menuList: (baseStyled, state) => {
    return {
      ...baseStyled,
      padding: 6,
      maxHeight: '260px'
    }
  },
  option: (base, state) => ({
    ...base,
    fontWeight: state.isSelected ? 500 : 400,
    fontSize: '16px',
    lineHeight: '20px',
    padding: '8px',
    backgroundColor: state.isSelected ? '#F0EFF3' : 'transparent',
    color: '#242424',
    overflow: 'hidden',
    maxWidth: '100%',
    textOverflow: 'ellipsis',
    borderRadius: 8,
    cursor: 'pointer',
    ':hover': {
      background: '#F7F7F9'
    }
  })
}

const SelectInput = memo(
  forwardRef((props, ref) => {
    const {
      name,
      placeholder,
      options,
      value,
      error,
      disabled = false,
      isSeparator = false,
      isMulti = false,
      onChange,
      customOption,
      customValue,
      customMultiValue,
      hideSelectedOptions = false,
      closeMenuOnSelect = true,
      icon,
      isClearable = false,
      customPositionMenu,
      ...other
    } = props

    const refContainer = useRef(null)
    const [currentValue, setCurrentValue] = useState(null)

    useEffect(() => {
      if (isMulti) {
        const values = currentValue?.map((item) => item.value)
        if (
          options.length &&
          (!values || JSON.stringify(values) !== JSON.stringify(value))
        ) {
          setCurrentValue(getCurrentValue(isMulti, value, options))
        }
        return
      } else {
        if (options?.length) {
          setCurrentValue(getCurrentValue(isMulti, value, options))
        }
      }
    }, [isMulti, value, options])

    const handleChange = (val, options) => {
      setCurrentValue(val)
      if (isMulti) {
        onChange(
          val?.map((item) => item?.value),
          options.name
        )
        return
      }
      if (val) {
        onChange(val?.value, options.name)
      } else {
        onChange('', options.name)
      }
    }

    return (
      <div className={styles.container} ref={refContainer}>
        <Select
          ref={ref}
          name={name}
          isMulti={isMulti}
          isDisabled={disabled}
          value={currentValue}
          components={getComponents(
            isMulti,
            customOption,
            customValue,
            customMultiValue
          )}
          onChange={handleChange}
          isError={!!error}
          hideSelectedOptions={hideSelectedOptions}
          closeMenuOnSelect={closeMenuOnSelect}
          isSeparator={isSeparator}
          placeholder={placeholder || ''}
          styles={selectStyles}
          options={options}
          icon={icon}
          isClearable={isClearable}
          isSearchable
          customPositionMenu={customPositionMenu}
          {...other}
        />
      </div>
    )
  })
)

export { SelectInput }
